import React, { useEffect, useMemo } from "react";
import { Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslate } from "../../../hooks/useTranslate";
import SubjectButton from "./SubjectsButton";
import subjectSelectionService from "../../../services/subjectSelectionService";
import axiosManagerRegister from "../../../services/axiosManagerRegister";
import { useToast } from "../../../context/toast";
import Preloader from "../../../components/Preloader";

export const HigherSubjectsSelect = (props) => {
    const {
        registrationId, childId, studySchedule,
        desiredStartDate,
        divisionSelected, yearGroupSelected, packageSelected, onPackageChange,
        subjectsSelected, onSubjectChange,
        onPrev, onNext,
    } = props;
    const toast = useToast();
    const { t } = useTranslate();

    const {
        isFetching: isLoadingProduct, data: product,
    } = useQuery({
        queryKey: [`add-student/${yearGroupSelected}/${divisionSelected}/packages`, studySchedule, desiredStartDate],
        queryFn: () => axiosManagerRegister.post$(`/api/api/registration/${registrationId}/product`, {
            studySchedule, divisionId: divisionSelected, yearGroupId: yearGroupSelected, startDate: desiredStartDate,
        }).then((response) => response?.data?.packages),
        retry: 0,
        enabled: !!packageSelected?.productId && !packageSelected?.name && !!yearGroupSelected && !!divisionSelected && !!registrationId && !!studySchedule,
    });

    useEffect(() => {
        if (packageSelected?.productId && !packageSelected.name && !!product?.length) {
            const fullPackage = product.find((item) => item.productId === packageSelected.productId);
            onPackageChange(fullPackage);
        }
    }, [product, packageSelected]);

    const {
        mutateAsync: confirmSubjects, isPending: isConfirmingSubjects,
    } = useMutation({
        mutationFn: () => axiosManagerRegister.post$(`/api/api/registration/${registrationId}/subject/confirm`, { childId }),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
    });

    const {
        mutateAsync: postSelectSubject, isPending: isPostingSelectSubject,
    } = useMutation({
        mutationFn: (requestData) => axiosManagerRegister.post$(`/api/api/registration/${registrationId}/subject`, requestData),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
    });
    const {
        mutateAsync: deleteSubject, isPending: isDeletingSubject,
    } = useMutation({
        mutationFn: (requestData) => axiosManagerRegister.delete$(`/api/api/registration/${registrationId}/subject`, requestData),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
    });

    const isActionDisabled = isPostingSelectSubject || isDeletingSubject || isConfirmingSubjects;

    const higherSubjectsToSelect = useMemo(() => {
        if (!packageSelected) return false;
        return subjectSelectionService.getHigherSubjectsToSelect(packageSelected, subjectsSelected);
    }, [packageSelected, packageSelected, subjectsSelected]);

    const onHigherSubjectClick = (subject, isHigher = false) => async () => {
        const { higherSubject } = subject;

        try {
            if (isHigher) {
                await deleteSubject({ onlineRegistrationId: registrationId, childId, subjectId: subject.productId });
                await postSelectSubject({
                    onlineRegistrationId: registrationId, childId, productId: packageSelected?.productId, subjectId: higherSubject.productId,
                });
                onSubjectChange([...subjectsSelected.filter((item) => item !== subject.productId), higherSubject.productId]);
            } else {
                await deleteSubject({ onlineRegistrationId: registrationId, childId, subjectId: higherSubject.productId });
                await postSelectSubject({
                    onlineRegistrationId: registrationId, childId, productId: packageSelected?.productId, subjectId: subject.productId,
                });
                onSubjectChange([...subjectsSelected.filter((item) => item !== higherSubject.productId), subject.productId]);
            }
        } catch {
            // Error is in Mutation
        }
    };
    const onNextClick = async () => {
        try {
            await confirmSubjects();
            onNext?.();
        } catch {
            //
        }
    };

    return (
        <div className="details-form">
            <Typography variant="h1">
                {t("addStudent.selectHigherSubjects")}
            </Typography>
            <Preloader isLoading={isLoadingProduct}>
                {(higherSubjectsToSelect || []).map((subject, idx) => (
                    <div className="w-full mt-4" key={subject.productId}>
                        <Typography variant="h5" component="div">
                            <b className="font-bold">{subject.name}</b>
                        </Typography>
                        <div className="flex flex-wrap align-stretch gap-4 mt-4">
                            <SubjectButton
                                key={subject.productId}
                                subject={subject}
                                packageProduct={packageSelected}
                                subjectsSelected={subjectsSelected}
                                isHigherSelection
                                disabled={isActionDisabled}
                                onSubjectClick={onHigherSubjectClick(subject)}
                            />
                            <SubjectButton
                                key={subject.higherSubject.productId}
                                subject={subject.higherSubject}
                                packageProduct={packageSelected}
                                subjectsSelected={subjectsSelected}
                                isHigherSelection
                                disabled={isActionDisabled}
                                onSubjectClick={onHigherSubjectClick(subject, true)}
                            />
                        </div>
                        {idx !== higherSubjectsToSelect.length - 1 && <div className="divider mt-4" />}
                    </div>
                ))}
            </Preloader>
            <div className="add-student__form-narrow flex align-center gap-8 mt-8">
                <Button
                    variant="outlined"
                    className="add-student-form__button"
                    disabled={isActionDisabled}
                    onClick={onPrev}
                >
                    {t("button.back")}
                </Button>
                <Button
                    variant="contained"
                    className="button-accent add-student-form__button"
                    disabled={isActionDisabled || !packageSelected}
                    loading={isConfirmingSubjects}
                    onClick={onNextClick}
                >
                    {t("button.next")}
                </Button>
            </div>
        </div>
    );
};
